/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './EBS.css';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';

const foldCourseText = (value) => String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/[çğıöşü]/g, (letter) => ({ ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u' }[letter]))
    .replace(/[^a-z0-9]+/g, '');

const isElectiveGroupCourse = (course) => {
    const type = foldCourseText(course?.course_type);
    const name = foldCourseText(course?.course_name);
    return (type.includes('secmeli') || type.includes('elective'))
        && (name.includes('secmeliders') || name.includes('electivecourse'));
};

const EBS = () => {
    const { t } = useTranslation();
    // Education Type States
    const [educationTypes, setEducationTypes] = useState([]);
    const [newEducationType, setNewEducationType] = useState({ name: '' });
    const [showAddEducationType, setShowAddEducationType] = useState(false);

    // Faculty States
    const [faculties, setFaculties] = useState([]);
    const [newFaculty, setNewFaculty] = useState({ name: '', educationTypeId: '' });
    const [showAddFaculty, setShowAddFaculty] = useState(false);

    // Program States
    const [programs, setPrograms] = useState([]);
    const [newProgram, setNewProgram] = useState({
        name: '',
        facultyId: '',
        durationYear: '',
        academicYear: ''
    });
    const [showAddProgram, setShowAddProgram] = useState(false);
    const [availableFaculties, setAvailableFaculties] = useState([]);

    // Course States
    const [courses, setCourses] = useState([]);
    const [coursesBySemester, setCoursesBySemester] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({}); // { "semester-courseId": true }
    const [newCourse, setNewCourse] = useState({
        code: '',
        name: '',
        semester: '',
        programId: '',
        courseType: '',
        teorik: '',
        uygulama: '',
        laboratuvar: '',
        kredi: '',
        akts: ''
    });
    const [showAddCourse, setShowAddCourse] = useState(false);

    // Common States
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    // Add new state for selected education type
    const [selectedEducationType, setSelectedEducationType] = useState(null);

    // Add new state for selected faculty
    const [selectedFaculty, setSelectedFaculty] = useState(null);

    // Add new state for selected program
    const [selectedProgram, setSelectedProgram] = useState(null);

    // Fetch all data on component mount
    useEffect(() => {
        fetchEducationTypes();
    }, []);

    // Add useEffect to fetch faculties when education type changes
    useEffect(() => {
        if (selectedEducationType) {
            fetchFaculties(selectedEducationType);
        }
    }, [selectedEducationType]);

    // Referanslar oluştur
    const facultySelectRef = useRef(null);
    const programSelectRef = useRef(null);

    // Eğitim türü seçildiğinde fakülte seçimine odaklan
    useEffect(() => {
        if (selectedEducationType && facultySelectRef.current) {
            setTimeout(() => {
                facultySelectRef.current.focus();
            }, 100);
        }
    }, [selectedEducationType]);

    // Fakülte seçildiğinde program seçimine odaklan
    useEffect(() => {
        if (selectedFaculty && programSelectRef.current) {
            setTimeout(() => {
                programSelectRef.current.focus();
            }, 100);
        }
    }, [selectedFaculty]);

    // Education Type Functions
    const fetchEducationTypes = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.EDUCATION_TYPES);
            setEducationTypes(response.data);
            setError('');
        } catch (err) {
            setError('Eğitim türleri yüklenirken bir hata oluştu.');
            console.error('Error fetching education types:', err);
        }
    };

    const handleAddEducationType = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await axios.post(API_ENDPOINTS.EDUCATION_TYPES, newEducationType);
            setSuccess('Eğitim türü başarıyla eklendi.');
            setNewEducationType({ name: '' });
            setShowAddEducationType(false);
            fetchEducationTypes();
        } catch (err) {
            setError('Eğitim türü eklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Faculty Functions
    const fetchFaculties = async (educationTypeId = null) => {
        try {
            setLoading(true);
            if (!educationTypeId) {
                setFaculties([]);
                setAvailableFaculties([]);
                return;
            }
            const response = await axios.get(`${API_BASE_URL}/api/faculties?educationTypeId=${educationTypeId}`);
            setFaculties(response.data);
            setAvailableFaculties(response.data);
            setError('');
        } catch (err) {
            console.error('Fakülteler yüklenirken hata:', err);
            setError(err.response?.data?.error || 'Fakülteler yüklenirken bir hata oluştu.');
            setFaculties([]);
            setAvailableFaculties([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddFaculty = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await axios.post(`${API_BASE_URL}/api/faculties`, newFaculty);
            setSuccess('Fakülte başarıyla eklendi.');
            setNewFaculty({ name: '', educationTypeId: '' });
            setShowAddFaculty(false);
            fetchFaculties();
        } catch (err) {
            setError('Fakülte eklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Program Functions
    const fetchPrograms = async (facultyId = null) => {
        try {
            setLoading(true);
            if (!facultyId) {
                setPrograms([]);
                return;
            }
            const response = await axios.get(`${API_BASE_URL}/api/programs?facultyId=${facultyId}`);
            setPrograms(response.data);
            setError('');
        } catch (err) {
            console.error('Programlar yüklenirken hata:', err);
            setError(err.response?.data?.error || 'Programlar yüklenirken bir hata oluştu.');
            setPrograms([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddProgram = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            // Seçilen fakültenin eğitim türünü bul
            const selectedFaculty = faculties.find(f => f.id === parseInt(newProgram.facultyId));
            if (!selectedFaculty) {
                throw new Error('Fakülte bulunamadı');
            }

            // Program verilerini hazırla
            const programData = {
                ...newProgram,
                educationTypeId: selectedFaculty.educationTypeId
            };

            await axios.post(`${API_BASE_URL}/api/programs`, programData);
            setSuccess('Program başarıyla eklendi.');
            setNewProgram({
                name: '',
                facultyId: '',
                durationYear: '',
                academicYear: ''
            });
            setShowAddProgram(false);
            fetchPrograms();
        } catch (err) {
            setError('Program eklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Course Functions
    const fetchCourses = async (programId = null) => {
        try {
            if (!programId) {
                setCourses([]);
                setCoursesBySemester({});
                return;
            }
            const response = await axios.get(`${API_BASE_URL}/api/courses?programId=${programId}`);
            setCourses(response.data);
            
            // Dönem bazlı dersleri de getir
            fetchCoursesBySemester(programId);
        } catch (err) {
            setError('Dersler yüklenirken bir hata oluştu.');
            setCourses([]);
            setCoursesBySemester({});
        }
    };

    const fetchCoursesBySemester = async (programId) => {
        try {
            if (!programId) {
                setCoursesBySemester({});
                return;
            }
            const response = await axios.get(`${API_BASE_URL}/api/programs/${programId}/courses-by-semester`);
            setCoursesBySemester(response.data);
        } catch (err) {
            console.error('Dönem bazlı dersler yüklenirken hata:', err);
            // Fallback: Normal courses endpoint'inden dersleri al ve dönem bazlı grupla
            try {
                const coursesResponse = await axios.get(`${API_BASE_URL}/api/courses?programId=${programId}`);
                const courses = coursesResponse.data;
                
                // Dönem bazlı gruplama yap
                const grouped = {};
                const regularCourses = [];
                const groups = [];
                
                // Önce dersleri kategorize et
                courses.forEach(course => {
                    // 0, yıllık/belirtilmemiş dersler için geçerli bir dönem
                    // değeridir; || kullanımı 0'ı kaybedip yanlış fallback'e
                    // düşürüyordu.
                    const semester = String(course.donem ?? course.semester ?? '0');
                    if (!grouped[semester]) {
                        grouped[semester] = [];
                    }
                    
                    const isGroup = isElectiveGroupCourse(course);
                    
                    if (isGroup) {
                        groups.push({...course, semester});
                    } else {
                        regularCourses.push({...course, semester});
                    }
                });
                
                // Normal dersleri ekle
                regularCourses.forEach(course => {
                    grouped[course.semester].push({
                        id: course.id,
                        course_code: course.course_code,
                        course_name: course.course_name,
                        course_type: course.course_type,
                        teorik: course.teorik || 0,
                        uygulama: course.uygulama || 0,
                        laboratuvar: course.laboratuvar || 0,
                        kredi: parseFloat(course.kredi) || 0,
                        akts: parseFloat(course.akts) || 0,
                        is_group: false
                    });
                });
                
                // Seçmeli ders gruplarını ekle (alt dersleri ile birlikte)
                groups.forEach(group => {
                    const courseData = {
                        id: group.id,
                        course_code: group.course_code,
                        course_name: group.course_name,
                        course_type: group.course_type,
                        teorik: group.teorik || 0,
                        uygulama: group.uygulama || 0,
                        laboratuvar: group.laboratuvar || 0,
                        kredi: parseFloat(group.kredi) || 0,
                        akts: parseFloat(group.akts) || 0,
                        is_group: true,
                        children: []
                    };
                    
                    grouped[group.semester].push(courseData);
                });
                
                setCoursesBySemester(grouped);
            } catch (fallbackErr) {
                console.error('Fallback de başarısız:', fallbackErr);
                setCoursesBySemester({});
            }
        }
    };

    const toggleGroup = (semester, courseId) => {
        const key = `${semester}-${courseId}`;
        setExpandedGroups(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleFileUpload = async (course, programId, file) => {
        if (!file) return;
        if (course.legacy_standalone) {
            setError('Bu eski seçmeli kayıt yalnız görüntülenebilir; yeni yılda Excel ile normal ders olarak açılacaktır.');
            return;
        }
        const courseId = course.raw_id || course.id;
        const uploadPath = course.source_type === 'elective_lesson'
            ? `/api/programs/${programId}/elective-lessons/${courseId}/upload-word`
            : `/api/programs/${programId}/courses/${courseId}/upload-word`;
        
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(
                `${API_BASE_URL}${uploadPath}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            if (response.data.success) {
                setSuccess('Word dosyası başarıyla yüklendi.');
                // Dosya input'unu temizle
                const fileInput = document.getElementById(`file-${courseId}`);
                if (fileInput) fileInput.value = '';
                // Dosya durumunu güncelle
                const statusElement = fileInput?.nextElementSibling?.nextElementSibling;
                if (statusElement) {
                    statusElement.textContent = file.name;
                }
            }
        } catch (err) {
            setError('Word dosyası yüklenirken bir hata oluştu.');
            console.error('File upload error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCourse = async (courseId, courseName) => {
        if (!window.confirm(t('ebs.deleteConfirm', { name: courseName }))) {
            return;
        }
        
        try {
            setLoading(true);
            const response = await axios.delete(`${API_BASE_URL}/api/courses/${courseId}`);
            
            if (response.data.success) {
                setSuccess(response.data.message || 'Ders başarıyla silindi.');
                // Dersleri yeniden yükle
                if (selectedProgram) {
                    fetchCourses(selectedProgram);
                }
            }
        } catch (err) {
            setError('Ders silinirken bir hata oluştu.');
            console.error('Delete course error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCourse = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            // Seçilen programın bilgilerini bul
            const selectedProgram = programs.find(p => p.id === parseInt(newCourse.programId));
            if (!selectedProgram) {
                throw new Error('Program bulunamadı');
            }

            // Ders verilerini hazırla
            const courseData = {
                ...newCourse,
                facultyId: selectedProgram.facultyId
            };

            await axios.post(`${API_BASE_URL}/api/courses`, courseData);
            setSuccess('Ders başarıyla eklendi.');
            setNewCourse({
                code: '',
                name: '',
                semester: '',
                programId: '',
                courseType: '',
                teorik: '',
                uygulama: '',
                laboratuvar: '',
                kredi: '',
                akts: ''
            });
            setShowAddCourse(false);
            fetchCourses();
        } catch (err) {
            setError('Ders eklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    // Add handler for education type click
    const handleEducationTypeClick = (educationType) => {
        const newSelectedEducationType = educationType.id === selectedEducationType ? null : educationType.id;
        setSelectedEducationType(newSelectedEducationType);
        setSelectedFaculty(null);
        setSelectedProgram(null);
        setFaculties([]);
        setAvailableFaculties([]);
        setPrograms([]);
        setCourses([]);
        if (newSelectedEducationType) {
            fetchFaculties(newSelectedEducationType);
        }
    };

    // Add handler for faculty click
    const handleFacultyClick = (faculty) => {
        const newSelectedFaculty = faculty.id === selectedFaculty ? null : faculty.id;
        setSelectedFaculty(newSelectedFaculty);
        setSelectedProgram(null);
        setPrograms([]);
        setCourses([]);
        if (newSelectedFaculty) {
            fetchPrograms(newSelectedFaculty);
        }
    };

    // Add handler for program click
    const handleProgramClick = (program) => {
        const newSelectedProgram = program.id === selectedProgram ? null : program.id;
        setSelectedProgram(newSelectedProgram);
        setCourses([]);
        if (newSelectedProgram) {
            fetchCourses(newSelectedProgram);
        }
    };

    return (
        <div className="ebs-container">
            <div className="page-header">
                <h1>{t('ebs.title')}</h1>
                <div className="header-buttons">
                    <button
                        className="toggle-button"
                        onClick={() => setShowAddEducationType(!showAddEducationType)}
                    >
                        {showAddEducationType ? t('ebs.closeEducationTypeForm') : t('ebs.addEducationType')}
                    </button>
                    <button
                        className="toggle-button"
                        onClick={() => setShowAddFaculty(!showAddFaculty)}
                    >
                        {showAddFaculty ? t('ebs.closeFacultyForm') : t('ebs.addFaculty')}
                    </button>
                    <button
                        className="toggle-button"
                        onClick={() => setShowAddProgram(!showAddProgram)}
                    >
                        {showAddProgram ? t('ebs.closeProgramForm') : t('ebs.addProgram')}
                    </button>
                    <button
                        className="toggle-button"
                        onClick={() => setShowAddCourse(!showAddCourse)}
                    >
                        {showAddCourse ? t('ebs.closeCourseForm') : t('ebs.addCourse')}
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Education Type Form */}
            {showAddEducationType && (
                <div className="form-section">
                    <h2>{t('ebs.newEducationType')}</h2>
                    <form onSubmit={handleAddEducationType}>
                        <div className="form-group">
                            <label>{t('ebs.educationTypeName')}</label>
                            <input
                                type="text"
                                value={newEducationType.name}
                                onChange={(e) => setNewEducationType({ name: e.target.value })}
                                placeholder={t('ebs.educationTypePlaceholder')}
                                required
                            />
                        </div>
                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? t('ebs.adding') : t('ebs.addEducationType')}
                        </button>
                    </form>
                </div>
            )}

            {/* Faculty Form */}
            {showAddFaculty && (
                <div className="form-section">
                    <h2>{t('ebs.newFaculty')}</h2>
                    <form onSubmit={handleAddFaculty}>
                        <div className="form-group">
                            <label>{t('ebs.facultyName')}</label>
                            <input
                                type="text"
                                value={newFaculty.name}
                                onChange={(e) => setNewFaculty({ ...newFaculty, name: e.target.value })}
                                placeholder={t('ebs.facultyPlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.educationTypeName').replace(':', '')}</label>
                            <select
                                value={newFaculty.educationTypeId}
                                onChange={(e) => setNewFaculty({ ...newFaculty, educationTypeId: e.target.value })}
                                required
                            >
                                <option value="">{t('ebs.selectEducationType')}</option>
                                {educationTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? t('ebs.adding') : t('ebs.addFaculty')}
                        </button>
                    </form>
                </div>
            )}

            {/* Program Form */}
            {showAddProgram && (
                <div className="form-section">
                    <h2>{t('ebs.newProgram')}</h2>
                    <form onSubmit={handleAddProgram}>
                        <div className="form-group">
                            <label>{t('ebs.programName')}</label>
                            <input
                                type="text"
                                value={newProgram.name}
                                onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })}
                                placeholder={t('ebs.programPlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.educationTypeName').replace(':', '')}</label>
                            <select
                                value={selectedEducationType || ''}
                                onChange={(e) => {
                                    const educationTypeId = e.target.value ? parseInt(e.target.value) : null;
                                    setSelectedEducationType(educationTypeId);
                                    setNewProgram({ ...newProgram, facultyId: '' });
                                    if (educationTypeId) {
                                        fetchFaculties(educationTypeId);
                                    }
                                }}
                                required
                            >
                                <option value="">{t('ebs.selectEducationType')}</option>
                                {educationTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {selectedEducationType && (
                            <div className="form-group animate-slide-in">
                                <label>{t('ebs.facultyName').replace(':', '')}</label>
                                <select
                                    ref={facultySelectRef}
                                    value={newProgram.facultyId}
                                    onChange={(e) => setNewProgram({ ...newProgram, facultyId: e.target.value })}
                                    required
                                >
                                    <option value="">{t('ebs.selectFaculty')}</option>
                                    {availableFaculties.map(faculty => (
                                        <option key={faculty.id} value={faculty.id}>
                                            {faculty.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="form-group">
                            <label>{t('ebs.durationYear')}</label>
                            <input
                                type="number"
                                value={newProgram.durationYear}
                                onChange={(e) => setNewProgram({ ...newProgram, durationYear: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.academicYear')}</label>
                            <input
                                type="text"
                                value={newProgram.academicYear}
                                onChange={(e) => setNewProgram({ ...newProgram, academicYear: e.target.value })}
                                placeholder={t('ebs.academicYearPlaceholder')}
                                required
                            />
                        </div>
                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? t('ebs.adding') : t('ebs.addProgram')}
                        </button>
                    </form>
                </div>
            )}

            {/* Course Form */}
            {showAddCourse && (
                <div className="form-section">
                    <h2>{t('ebs.newCourse')}</h2>
                    <form onSubmit={handleAddCourse}>
                        <div className="form-group">
                            <label>{t('ebs.educationTypeName').replace(':', '')}</label>
                            <select
                                value={selectedEducationType || ''}
                                onChange={(e) => {
                                    const educationTypeId = e.target.value ? parseInt(e.target.value) : null;
                                    setSelectedEducationType(educationTypeId);
                                    setNewCourse({ ...newCourse, programId: '' });
                                    if (educationTypeId) {
                                        fetchFaculties(educationTypeId);
                                    }
                                }}
                                required
                            >
                                <option value="">{t('ebs.selectEducationType')}</option>
                                {educationTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {selectedEducationType && (
                            <div className="form-group animate-slide-in">
                                <label>{t('ebs.facultyName').replace(':', '')}</label>
                                <select
                                    ref={facultySelectRef}
                                    value={selectedFaculty || ''}
                                    onChange={(e) => {
                                        const facultyId = e.target.value ? parseInt(e.target.value) : null;
                                        setSelectedFaculty(facultyId);
                                        setNewCourse({ ...newCourse, programId: '' });
                                        if (facultyId) {
                                            fetchPrograms(facultyId);
                                        }
                                    }}
                                    required
                                >
                                    <option value="">{t('ebs.selectFaculty')}</option>
                                    {availableFaculties.map(faculty => (
                                        <option key={faculty.id} value={faculty.id}>
                                            {faculty.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {selectedFaculty && (
                            <div className="form-group animate-slide-in">
                                <label>{t('ebs.programName').replace(':', '')}</label>
                                <select
                                    ref={programSelectRef}
                                    value={newCourse.programId}
                                    onChange={(e) => setNewCourse({ ...newCourse, programId: e.target.value })}
                                    required
                                >
                                    <option value="">{t('ebs.selectProgram')}</option>
                                    {programs.map(program => (
                                        <option key={program.id} value={program.id}>
                                            {program.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="form-group">
                            <label>{t('ebs.courseCode')}</label>
                            <input
                                type="text"
                                value={newCourse.code}
                                onChange={(e) => setNewCourse({ ...newCourse, code: e.target.value })}
                                placeholder={t('ebs.courseCodePlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.courseName')}</label>
                            <input
                                type="text"
                                value={newCourse.name}
                                onChange={(e) => setNewCourse({ ...newCourse, name: e.target.value })}
                                placeholder={t('ebs.courseNamePlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.semesterNo')}</label>
                            <select
                                value={newCourse.semester}
                                onChange={(e) => setNewCourse({ ...newCourse, semester: e.target.value })}
                                required
                            >
                                <option value="">{t('ebs.selectSemesterNo')}</option>
                                {[...Array(11)].map((_, index) => (
                                    <option key={index} value={index}>
                                        {index}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.courseType')}</label>
                            <select
                                value={newCourse.courseType}
                                onChange={(e) => setNewCourse({ ...newCourse, courseType: e.target.value })}
                                required
                            >
                                <option value="">{t('ebs.selectCourseType')}</option>
                                <option value="Zorunlu">{t('ebs.compulsory')}</option>
                                <option value="Seçmeli">{t('ebs.elective')}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.theoreticalHours')}</label>
                            <input
                                type="number"
                                value={newCourse.teorik}
                                onChange={(e) => setNewCourse({ ...newCourse, teorik: e.target.value })}
                                required
                                min="0"
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.practiceHours')}</label>
                            <input
                                type="number"
                                value={newCourse.uygulama}
                                onChange={(e) => setNewCourse({ ...newCourse, uygulama: e.target.value })}
                                required
                                min="0"
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.laboratoryHours')}</label>
                            <input
                                type="number"
                                value={newCourse.laboratuvar}
                                onChange={(e) => setNewCourse({ ...newCourse, laboratuvar: e.target.value })}
                                required
                                min="0"
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.credit')}</label>
                            <input
                                type="number"
                                value={newCourse.kredi}
                                onChange={(e) => setNewCourse({ ...newCourse, kredi: e.target.value })}
                                required
                                min="0"
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('ebs.ects')}</label>
                            <input
                                type="number"
                                value={newCourse.akts}
                                onChange={(e) => setNewCourse({ ...newCourse, akts: e.target.value })}
                                required
                                min="0"
                            />
                        </div>
                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? t('ebs.adding') : t('ebs.addCourse')}
                        </button>
                    </form>
                </div>
            )}

            {/* Data Display Sections */}
            <div className="data-sections">
                <div className="data-section">
                    <h2>{t('ebs.educationTypes')}</h2>
                    <div className="data-list">
                        {educationTypes.map(type => (
                            <div
                                key={type.id}
                                className={`data-item ${selectedEducationType === type.id ? 'selected' : ''}`}
                                onClick={() => handleEducationTypeClick(type)}
                                style={{ cursor: 'pointer' }}
                            >
                                <span>{type.name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="data-section">
                    <h2>{t('ebs.faculties')}</h2>
                    <div className="data-list">
                        {!selectedEducationType ? (
                            <div className="no-data">{t('ebs.selectEducationTypePrompt')}</div>
                        ) : faculties.length === 0 ? (
                            <div className="no-data">{t('ebs.noFacultyFound')}</div>
                        ) : (
                            faculties.map(faculty => (
                                <div
                                    key={faculty.id}
                                    className={`data-item ${selectedFaculty === faculty.id ? 'selected' : ''}`}
                                    onClick={() => handleFacultyClick(faculty)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span>{faculty.name}</span>
                                    <span className="sub-text">
                                        {educationTypes.find(type => type.id === faculty.educationTypeId)?.name}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="data-section">
                    <h2>{t('ebs.programs')}</h2>
                    <div className="data-list">
                        {!selectedFaculty ? (
                            <div className="no-data">{t('ebs.selectFacultyPrompt')}</div>
                        ) : programs.length === 0 ? (
                            <div className="no-data">{t('ebs.noProgramFound')}</div>
                        ) : (
                            programs.map(program => (
                                <div
                                    key={program.id}
                                    className={`data-item ${selectedProgram === program.id ? 'selected' : ''}`}
                                    onClick={() => handleProgramClick(program)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span>{program.name}</span>
                                    <span className="sub-text">
                                        {faculties.find(f => f.id === program.facultyId)?.name} -
                                        {educationTypes.find(type => type.id === program.educationTypeId)?.name}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="data-section">
                    <h2>{t('ebs.courses')}</h2>
                    {!selectedProgram ? (
                        <div className="no-data">{t('ebs.selectProgramPrompt')}</div>
                    ) : Object.keys(coursesBySemester).length === 0 ? (
                        <div className="no-data">{t('ebs.noCourseFound')}</div>
                    ) : (
                        Object.keys(coursesBySemester).sort((a, b) => parseInt(a) - parseInt(b)).map(semester => (
                            <div key={semester} className="semester-section">
                                <h3 className="semester-title">{t('ebs.semester')} {semester}</h3>
                                <div className="courses-table-container">
                                    <table className="courses-table">
                                        <thead>
                                            <tr>
                                                <th>{t('ebs.courseCodeHeader')}</th>
                                                <th>{t('ebs.courseNameHeader')}</th>
                                                <th>{t('ebs.courseTypeHeader')}</th>
                                                <th>{t('ebs.theoretical')}</th>
                                                <th>{t('ebs.practice')}</th>
                                                <th>{t('ebs.laboratory')}</th>
                                                <th>{t('ebs.credits')}</th>
                                                <th>{t('ebs.ectsHeader')}</th>
                                                <th>{t('ebs.uploadWord')}</th>
                                                <th>{t('ebs.delete')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {coursesBySemester[semester].map(course => (
                                                <React.Fragment key={course.id}>
                                                    <tr className={course.is_group ? 'course-group-row' : 'course-row'}>
                                                        <td>
                                                            {course.is_group && (
                                                                <span
                                                                    className="expand-icon"
                                                                    onClick={() => toggleGroup(semester, course.id)}
                                                                >
                                                                    {expandedGroups[`${semester}-${course.id}`] ? '▼' : '▶'}
                                                                </span>
                                                            )}
                                                            {course.course_code}
                                                        </td>
                                                        <td>{course.course_name}</td>
                                                        <td>
                                                            {course.course_type === 'Zorunlu' ? t('ebs.courseTypeCompulsory')
                                                                : course.course_type === 'Seçmeli' ? t('ebs.courseTypeElective')
                                                                : course.course_type}
                                                        </td>
                                                        <td>{course.teorik}</td>
                                                        <td>{course.uygulama}</td>
                                                        <td>{course.laboratuvar}</td>
                                                        <td>{course.kredi}</td>
                                                        <td>{course.akts}</td>
                                                        <td>
                                                            {course.is_group || course.legacy_standalone ? (
                                                                <span className="no-action-text">
                                                                    {course.legacy_standalone ? 'Eski seçmeli — görüntüleme' : t('ebs.noSubcourse')}
                                                                </span>
                                                            ) : (
                                                                <div className="file-upload-container">
                                                                    <input
                                                                        type="file"
                                                                        accept=".doc,.docx"
                                                                        className="file-input"
                                                                        id={`file-${course.id}`}
                                                                        onChange={(e) => {
                                                                            const file = e.target.files[0];
                                                                            if (file && selectedProgram) {
                                                                                handleFileUpload(course, selectedProgram, file);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <label htmlFor={`file-${course.id}`} className="file-label">
                                                                        {t('ebs.selectFile')}
                                                                    </label>
                                                                    <span className="file-status">{t('ebs.noFileSelected')}</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {!course.is_group && course.source_type !== 'elective_lesson' && (
                                                                <button
                                                                    className="delete-btn"
                                                                    onClick={() => handleDeleteCourse(course.id, course.course_name)}
                                                                >
                                                                    {t('ebs.delete')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {course.is_group && expandedGroups[`${semester}-${course.id}`] && course.children && course.children.map(child => (
                                                        <tr key={child.id} className="course-child-row">
                                                            <td>{child.course_code}</td>
                                                            <td>{child.course_name}</td>
                                                            <td>
                                                                {child.course_type === 'Zorunlu' ? t('ebs.courseTypeCompulsory')
                                                                    : child.course_type === 'Seçmeli' ? t('ebs.courseTypeElective')
                                                                    : child.course_type}
                                                            </td>
                                                            <td>{child.teorik}</td>
                                                            <td>{child.uygulama}</td>
                                                            <td>{child.laboratuvar}</td>
                                                            <td>{child.kredi}</td>
                                                            <td>{child.akts}</td>
                                                            <td>
                                                                <div className="file-upload-container">
                                                                    <input
                                                                        type="file"
                                                                        accept=".doc,.docx"
                                                                        className="file-input"
                                                                        id={`file-${child.id}`}
                                                                        onChange={(e) => {
                                                                            const file = e.target.files[0];
                                                                            if (file && selectedProgram) {
                                                                                handleFileUpload(child, selectedProgram, file);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <label htmlFor={`file-${child.id}`} className="file-label">
                                                                        {t('ebs.selectFile')}
                                                                    </label>
                                                                    <span className="file-status">{t('ebs.noFileSelected')}</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="no-action-text">Alt ders</span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default EBS; 
